import { useEffect, useMemo } from 'react';
import { Box, CircularProgress, Alert, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { CombinedPlot, CHANNEL_COLOR_MAP } from '@/components/MeasurementAnalysis/Charts/CombinedPlot';
import { ClassificationPanel } from '@/components/MeasurementAnalysis/Charts/ClassificationPanel';
import { useChartData } from '@/components/MeasurementAnalysis/Charts/useChartData';
import type { PathogenClassificationResult } from '@/services/api/testService';
import { useHasAnyRole } from '@/store/useAuthStore';
import { referenceLabelService } from '@/services/api/referenceLabelService';
import { features } from '@/config/features';
import type { ReferenceLabelDto } from '@/types/referenceLabels';

export interface TestChartOverlayProps {
  testId: string | null;
  open: boolean;
  onClose: () => void;
  pathogenClassificationResults?: PathogenClassificationResult[];
}

export function TestChartOverlay({
  testId,
  open,
  onClose,
  pathogenClassificationResults,
}: TestChartOverlayProps) {
  const { testData, isLoading, error } = useChartData(open && testId ? [testId] : []);

  const isResearcher = useHasAnyRole('researcher', 'admin');

  const { data: referenceLabelData } = useQuery({
    queryKey: ['reference-labels', testId],
    queryFn: () => referenceLabelService.getLabels(testId!),
    enabled: open && testId != null && isResearcher && features.enableResearchFeatures,
    staleTime: 5 * 60 * 1000,
  });

  const referenceLabelsMap = useMemo(() => {
    if (!referenceLabelData) return undefined;
    const map = new Map<number, ReferenceLabelDto>();
    for (const l of referenceLabelData) {
      if (l.referenceLabel != null) map.set(l.channelId, l);
    }
    return map;
  }, [referenceLabelData]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Box
      data-testid="test-chart-overlay"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        bgcolor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '2vh 2vw',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            minHeight: 52,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            Combined Chart
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close chart overlay">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {isLoading && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
          {error && !isLoading && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">Failed to load chart data. Please try again.</Alert>
            </Box>
          )}
          {!isLoading && !error && testData.length > 0 && (
            <>
              <Box sx={{ flex: 1, overflow: 'hidden', px: 2, py: 1, minHeight: 0 }}>
                <CombinedPlot
                  testData={testData}
                  visibleChannels={new Set(['CH1', 'CH2', 'CH3', 'CH4'])}
                  phaseUnit="deg"
                  yScale="linear"
                />
              </Box>
              {pathogenClassificationResults && pathogenClassificationResults.length > 0 && (
                <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
                  <ClassificationPanel
                    results={pathogenClassificationResults}
                    channelColorMap={CHANNEL_COLOR_MAP}
                    referenceLabels={referenceLabelsMap}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
